import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CampaignContext } from "../Context";
import {
  convertESTtoUserLocalDateAndTime,
  convertESTtoUserLocalTime,
  convertToEstTime,
  isTruthy,
} from "helpers/methods";
import { cloneDeep } from "lodash";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import DateTimePicker from "./DateTimePicker";

const ScheduleSection = () => {
  const { managingStep, setManagingStep, submitCampaign, setSubmitCampaign } =
    useContext(CampaignContext);
  const now = new Date();
  const [formFields, setFormFields] = useState<{
    scheduleType: string;
    date: { value: Date | null; error: string };
    time: { value: Date | null; error: string };
  }>({
    scheduleType: "",
    date: {
      value: now ?? null,
      error: "",
    },
    time: {
      value: new Date(now.getTime() + 5 * 60000) ?? null,
      error: "",
    },
  });

  const scheduleTime = moment(submitCampaign?.campaign?.scheduleTime).format(
    "MM/DD/YYYY HH:mm:ss"
  );

  const checkMessageIdForUpdateData =
    (!isTruthy(submitCampaign?.campaignData?.messageId) &&
      submitCampaign.campaign?.status === "Submitted") ||
    submitCampaign.campaign?.status === "Draft";
  moment(
    convertESTtoUserLocalDateAndTime(
      scheduleTime,
      "MM/DD/YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss"
    )
  ).isAfter(moment().format("MM/DD/YYYY HH:mm:ss"));

  useEffect(() => {
    if (checkMessageIdForUpdateData && submitCampaign?.campaign?.scheduleTime) {
      const scheduleTimeString = submitCampaign.campaign.scheduleTime;

      const isValidFormat = moment(
        scheduleTimeString,
        "MM/DD/YYYY HH:mm:ss",
        true
      ).isValid();

      const convertedScheduleTime = convertESTtoUserLocalTime(
        scheduleTimeString,
        isValidFormat ? "MM/DD/YYYY HH:mm:ss" : "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD HH:mm:ss"
      );
      if (convertedScheduleTime) {
        const scheduleDate = moment(
          convertedScheduleTime,
          "YYYY-MM-DD HH:mm:ss"
        ).toDate();

        setFormFields((prevFields) => ({
          ...prevFields,
          date: {
            value: scheduleDate,
            error: "",
          },
          time: {
            value: scheduleDate,
            error: "",
          },
        }));
      }
    }
  }, [checkMessageIdForUpdateData]);

  const handleSave = async () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!clonedFormFields.date.value) {
      error = true;
      clonedFormFields.date.error = "Please select the date.";
    }
    if (moment().isAfter(clonedFormFields.date.value, "date")) {
      error = true;
      clonedFormFields.date.error =
        "Selected date is already past. Please select a future date.";
    }
    if (!clonedFormFields.time.value) {
      error = true;
      clonedFormFields.time.error = "Please select the time.";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }
    const date = new Date(formFields.date.value!);
    const time = new Date(formFields.time.value!);
    let scheduleTimeIST = moment(date)
      .set({
        hour: time.getHours(),
        minute: time.getMinutes(),
        second: time.getSeconds(),
      })
      .tz("Asia/Kolkata")
      .format("MM/DD/YYYY HH:mm:ss");
    const scheduleTimeEST = convertToEstTime(
      scheduleTimeIST,
      "MM/DD/YYYY HH:mm:ss"
    );
    setSubmitCampaign({
      ...submitCampaign,
      campaign: {
        ...submitCampaign.campaign,
        scheduleTime: scheduleTimeEST,
      },
    });
    setManagingStep("");
  };

  const stepCompletion = () => {
    return submitCampaign.campaign.scheduleTime;
  };

  const getExpandedView = () => {
    return (
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={4} width="100%">
            <SvgIcon>
              <CheckCircleIcon
                htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
              />
            </SvgIcon>
            <Stack spacing={2} width="100%">
              <Stack spacing={0.5}>
                <Typography variant="h4">Sending time</Typography>
                <Typography variant="body1">
                  Set the sending time for the campaign
                </Typography>
              </Stack>
              <DateTimePicker
                formFields={formFields}
                setFormFields={setFormFields}
              />
            </Stack>
          </Stack>
          <Stack>
            <IconButton onClick={() => setManagingStep("")}>
              <SvgIcon>
                <CloseIcon />
              </SvgIcon>
            </IconButton>
          </Stack>
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={1}
        >
          <Button
            size="small"
            variant="text"
            onClick={() => setManagingStep("")}
          >
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getNonExpandedView = () => {
    const onAnotherStep =
      managingStep !== "" && managingStep !== "schedule-section";
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ opacity: onAnotherStep ? 0.5 : 1 }}
      >
        <Stack direction="row" spacing={4}>
          <SvgIcon>
            <CheckCircleIcon
              htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
            />
          </SvgIcon>
          <Stack spacing={0.5}>
            <Typography variant="h4">Sending time</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body1">
                Set the sending time for the campaign
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setManagingStep("schedule-section");
            }}
            disabled={onAnotherStep}
          >
            Edit Sending Time
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getPage = () => {
    if (managingStep === "schedule-section") {
      return getExpandedView();
    }
    return getNonExpandedView();
  };

  return <Box px={3}>{getPage()}</Box>;
};

export default ScheduleSection;
